import React from 'react';
import { AddEventItem } from '../../../../../../model/item';
import { VenueSection } from '../../../../../../model/venue';
import { SearchSelectOption, SearchSelect } from '../../../../../../components/Form/Select/SearchSelect';
import { t } from '../../../../../../locales/translate';
import { TextField } from '../../../../../../components/Form/TextField/TextField';
import { Select } from '../../../../../../components/Form/Select/Select';
import { uniq } from 'lodash';

interface TicketsSelectProps {
  model: AddEventItem;
  selectedSection: VenueSection;
}

function seats(model: AddEventItem, selectedSection: VenueSection): SearchSelectOption[] {
  return selectedSection.seats
    .map((seat) => {
      return {
        ...seat,
        status: model.event?.seatStatus?.[seat.id]?.status,
      };
    })
    .filter((seat) => seat.status === undefined || seat.status === 'available' || seat.status === 'blocked')
    .sort((a, b) => Number(a.row) - Number(b.row) || Number(a.name) - Number(b.name))
    .map((seat) => ({
      id: seat.id,
      label: `${seat.row} / ${seat.name}${seat.status === 'blocked' ? ` (${t.model.status.blocked})` : ''}`,
    }));
}

export function TicketsSelect({ model, selectedSection }: TicketsSelectProps): JSX.Element {
  const selectedSeats = selectedSection?.seats?.filter((e) => model.seatIds?.includes(e.id)) ?? [];
  const selectedSeatsPricingCategoryId = uniq(selectedSeats.map((e) => e.pricingCategoryId)).join('/');
  const pricingCategories = (model.selectedVariants ?? [])
    .filter(
      (variant) => selectedSection?.generalAdmission || variant.pricingCategoryId === selectedSeatsPricingCategoryId
    )
    .map((variant) => ({
      value: variant.id,
      label: `${variant.categoryName} / ${variant.description} - ${
        t.model.ticket[variant.physical ? 'hardticket' : 'onlineticket']
      }`,
    }));

  const sectionSeats = seats(model, selectedSection);

  if (selectedSection?.generalAdmission) {
    return (
      <>
        <TextField name="count" label={t.model.ticket.numberOfTickets} type="number" min={1} required autoFocus />
        {!!model.count && Number(model.count) > 0 && (
          <Select
            name="variantId"
            required
            label={t.model.product.productVariant}
            options={pricingCategories}
            allowEmpty
          />
        )}
      </>
    );
  } else if (sectionSeats.length > 0) {
    return (
      <>
        <SearchSelect name="seatIds" required label={t.model.venueLayout.seats} values={sectionSeats} />
        {!!model.seatIds?.length && (
          <Select
            name="variantId"
            required
            label={t.model.product.productVariant}
            options={pricingCategories}
            allowEmpty
          />
        )}
        {model.event?.personalizationRequirement && (
          <TextField name="personalization" label={t.model.event.personalization._name} />
        )}
      </>
    );
  } else {
    return <span>{t.model.event.noSeatsAvailable}</span>;
  }
}
